import { auth } from './firebase'

const API_BASE_URL = 'https://us-central1-vapourvape-loyalty-prod.cloudfunctions.net/api'

class ApiService {
  private async getAuthToken(): Promise<string> {
    const user = auth.currentUser
    if (!user) {
      throw new Error('Not authenticated')
    }
    return await user.getIdToken()
  }

  private getStaffSession() {
    const staffSession = localStorage.getItem('staffSession')
    if (!staffSession) {
      throw new Error('Staff not authenticated')
    }
    return JSON.parse(staffSession)
  }

  private getStaffAuthHeaders() {
    const staffSession = this.getStaffSession()
    return {
      'Content-Type': 'application/json',
      'x-staff-session': JSON.stringify(staffSession.session)
    }
  }

  async staffScan(customerCode: string, _staffId: string, _staffName: string, stampsToAdd: number = 1) {
    try {
      const headers = this.getStaffAuthHeaders()
      
      const response = await fetch(`${API_BASE_URL}/staff/scan-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          customerCode,
          stampsToAdd
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to process scan')
      }

      return await response.json()
    } catch (error) {
      console.error('Staff scan error:', error)
      throw error
    }
  }

  async staffRedeem(customerCode: string, _staffId: string, _staffName: string) {
    try {
      const headers = this.getStaffAuthHeaders()
      
      const response = await fetch(`${API_BASE_URL}/staff/redeem-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          customerCode
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to process redemption')
      }

      return await response.json()
    } catch (error) {
      console.error('Staff redeem error:', error)
      throw error
    }
  }

  async getUserByCode(code: string) {
    try {
      const headers = this.getStaffAuthHeaders()
      
      const response = await fetch(`${API_BASE_URL}/staff/users/${code}`, {
        headers
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'User not found')
      }

      return await response.json()
    } catch (error) {
      console.error('User lookup error:', error)
      throw error
    }
  }

  async getCustomers(filters: {
    page?: number
    limit?: number
    search?: string
    minStamps?: number
    maxStamps?: number
  } = {}) {
    const token = await this.getAuthToken()
    const queryParams = new URLSearchParams()
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString())
      }
    })

    const response = await fetch(`${API_BASE_URL}/admin/customers?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to fetch customers')
    }

    return await response.json()
  }

  async createGoogleWalletPass(userId: string) {
    const token = await this.getAuthToken()
    
    const response = await fetch(`${API_BASE_URL}/google-wallet/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userId })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to create Google Wallet pass')
    }

    return await response.json()
  }

  async sendCustomPasswordReset(email: string) {
    const response = await fetch(`${API_BASE_URL}/email/password-reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to send password reset email')
    }

    return await response.json()
  }

  async sendCustomEmailVerification(email: string) {
    const response = await fetch(`${API_BASE_URL}/email/verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to send email verification')
    }

    return await response.json()
  }
}

export const api = new ApiService()